import React, { useEffect, useState } from "react";
import { api, formatApiError } from "../lib/api";
import { toast } from "sonner";
import { Save, Loader2, Route, MapPin, CheckCircle2, AlertTriangle } from "lucide-react";

const BLANK = {
  citta_base: "", consumo_km_l: 12, prezzo_carburante_l: 1.85,
  highway_factor: 0.6, tariffa_casello_per_km: 0.08,
  andata_ritorno: true, distanza_factor: 1.3,
};

export default function TrasfertaConfig() {
  const [data, setData] = useState(BLANK);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [resolved, setResolved] = useState({ lat: null, lon: null, label: "" });

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/settings/trasferta");
      setData({
        citta_base: data.citta_base || "",
        consumo_km_l: data.consumo_km_l ?? 12,
        prezzo_carburante_l: data.prezzo_carburante_l ?? 1.85,
        highway_factor: data.highway_factor ?? 0.6,
        tariffa_casello_per_km: data.tariffa_casello_per_km ?? 0.08,
        andata_ritorno: data.andata_ritorno !== false,
        distanza_factor: data.distanza_factor ?? 1.3,
      });
      setResolved({ lat: data.citta_base_lat, lon: data.citta_base_lon, label: data.citta_base_label });
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const { data: res } = await api.put("/settings/trasferta", {
        ...data,
        consumo_km_l: Number(data.consumo_km_l) || 12,
        prezzo_carburante_l: Number(data.prezzo_carburante_l) || 1.85,
        highway_factor: Number(data.highway_factor) || 0,
        tariffa_casello_per_km: Number(data.tariffa_casello_per_km) || 0,
        distanza_factor: Number(data.distanza_factor) || 1.3,
      });
      setResolved({ lat: res.citta_base_lat, lon: res.citta_base_lon, label: res.citta_base_label });
      if (data.citta_base && !res.citta_base_lat) {
        toast.warning("Città base non riconosciuta dalla geocodifica. Prova un nome più specifico.");
      } else {
        toast.success("Configurazione trasferte salvata");
      }
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setSaving(false); }
  };

  if (loading) return <div className="card-shell p-6"><Loader2 className="w-4 h-4 animate-spin" /></div>;

  return (
    <div className="card-shell p-6" data-testid="trasferta-config">
      <div className="flex items-center gap-2 mb-4">
        <Route className="w-5 h-5" style={{ color: "var(--primary)" }} />
        <h2 className="font-bold text-lg" style={{ fontFamily: "Manrope" }}>Trasferte e meteo</h2>
      </div>
      <p className="text-sm mb-5" style={{ color: "var(--text-muted)" }}>
        Configura la città di partenza e i parametri usati per calcolare distanza, consumi carburante,
        costi caselli e meteo nella pagina <strong>Pianificazione interventi</strong>.
        Le previsioni meteo arrivano da <a href="https://open-meteo.com" target="_blank" rel="noreferrer" className="underline">Open-Meteo</a> (gratis, fino a 16 giorni).
      </p>

      <form onSubmit={save} className="space-y-4">
        <div>
          <label className="text-xs font-semibold uppercase tracking-wider" style={{ color: "var(--text-muted)" }}>Città base (partenza) *</label>
          <input value={data.citta_base} onChange={(e) => setData({ ...data, citta_base: e.target.value })}
            placeholder="Es. Milano, Roma, Torino…"
            className="mt-1 w-full px-3 py-2 text-sm rounded-md border outline-none"
            style={{ borderColor: "var(--border)", background: "#fff" }}
            data-testid="tr-citta-base" />
          {resolved.lat ? (
            <div className="mt-1 text-xs inline-flex items-center gap-1" style={{ color: "#16A34A" }} data-testid="tr-resolved">
              <CheckCircle2 className="w-3.5 h-3.5" /> Geocodificata: {resolved.label} ({resolved.lat.toFixed(4)}, {resolved.lon.toFixed(4)})
            </div>
          ) : data.citta_base ? (
            <div className="mt-1 text-xs inline-flex items-center gap-1" style={{ color: "#B45309" }}>
              <AlertTriangle className="w-3.5 h-3.5" /> Non ancora geocodificata. Salva per provare la risoluzione.
            </div>
          ) : null}
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <NumField label="Consumo veicolo (km/L)" value={data.consumo_km_l} step="0.1"
            onChange={(v) => setData({ ...data, consumo_km_l: v })} testid="tr-consumo"
            helper="Quanti km percorre il veicolo con un litro" />
          <NumField label="Prezzo carburante (€/L)" value={data.prezzo_carburante_l} step="0.01"
            onChange={(v) => setData({ ...data, prezzo_carburante_l: v })} testid="tr-prezzo" />
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <NumField label="% km su autostrada" value={data.highway_factor} step="0.05" min="0" max="1"
            onChange={(v) => setData({ ...data, highway_factor: v })} testid="tr-hwy"
            helper="0.6 = 60% del percorso in autostrada (stima)" />
          <NumField label="Tariffa casello (€/km)" value={data.tariffa_casello_per_km} step="0.01"
            onChange={(v) => setData({ ...data, tariffa_casello_per_km: v })} testid="tr-tariffa"
            helper="Tariffa media autostradale (€0,07–0,10)" />
          <NumField label="Fattore distanza stradale" value={data.distanza_factor} step="0.05" min="1"
            onChange={(v) => setData({ ...data, distanza_factor: v })} testid="tr-factor"
            helper="1.3 = +30% rispetto alla linea retta" />
        </div>

        <label className="flex items-center gap-2 text-sm cursor-pointer">
          <input type="checkbox" checked={!!data.andata_ritorno}
            onChange={(e) => setData({ ...data, andata_ritorno: e.target.checked })}
            data-testid="tr-andata-ritorno" />
          Calcola sempre andata + ritorno (raddoppia km)
        </label>

        <div className="flex justify-end pt-2">
          <button type="submit" disabled={saving} className="btn-primary disabled:opacity-60" data-testid="tr-save">
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
            Salva configurazione
          </button>
        </div>
      </form>

      {/* Live preview */}
      {resolved.lat && (
        <div className="mt-5 p-3 rounded-md text-xs" style={{ background: "var(--surface-muted)" }} data-testid="tr-preview">
          <div className="font-semibold mb-1 inline-flex items-center gap-1"><MapPin className="w-3 h-3" /> Esempio: viaggio di 100 km da {resolved.label}</div>
          <div style={{ color: "var(--text-muted)" }}>
            {data.andata_ritorno ? "200 km a/r" : "100 km solo andata"} ·
            {" "}{((data.andata_ritorno ? 200 : 100) / (Number(data.consumo_km_l) || 12)).toFixed(2)}L carburante ·
            {" "}€ {(((data.andata_ritorno ? 200 : 100) / (Number(data.consumo_km_l) || 12)) * (Number(data.prezzo_carburante_l) || 1.85)).toFixed(2)} carburante +
            {" "}€ {((data.andata_ritorno ? 200 : 100) * Number(data.highway_factor) * Number(data.tariffa_casello_per_km)).toFixed(2)} caselli
          </div>
        </div>
      )}
    </div>
  );
}

function NumField({ label, value, onChange, testid, helper, step, min, max }) {
  return (
    <div>
      <label className="text-xs font-semibold uppercase tracking-wider" style={{ color: "var(--text-muted)" }}>{label}</label>
      <input type="number" step={step} min={min} max={max} value={value}
        onChange={(e) => onChange(e.target.value)}
        className="mt-1 w-full px-3 py-2 text-sm rounded-md border outline-none"
        style={{ borderColor: "var(--border)", background: "#fff" }}
        data-testid={testid} />
      {helper && <div className="text-xs mt-1" style={{ color: "var(--text-muted)" }}>{helper}</div>}
    </div>
  );
}
